import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import { isAxiosError } from "axios";
import { useToast } from "../../shared/toast";
import { useKardaAdmin } from "./KardaAdminContext";

export interface StationDetailOutletContext {
  setHeaderAction: (action: React.ReactNode) => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const KardaStationDetailPage: React.FC = () => {
  const [headerAction, setHeaderAction] = useState<React.ReactNode>(null);
  const stableSetHeaderAction = useCallback((action: React.ReactNode) => {
    setHeaderAction(action);
  }, []);
  const { stationId } = useParams<{ stationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, "");

  const BASE_PATH = `/karda-admin/stations/${stationId}`;
  const NAV_ITEMS: NavItem[] = useMemo(
    () => [
      {
        label: "Station Details",
        path: BASE_PATH,
        icon: <InfoOutlinedIcon fontSize="small" />,
      },
      {
        label: "Station Users",
        path: `${BASE_PATH}/users`,
        icon: <PeopleOutlinedIcon fontSize="small" />,
      },
      {
        label: "Bodycam Integration",
        path: `${BASE_PATH}/bodycam`,
        icon: <VideocamOutlinedIcon fontSize="small" />,
      },
      {
        label: "Settings",
        path: `${BASE_PATH}/settings`,
        icon: <SettingsOutlinedIcon fontSize="small" />,
      },
    ],
    [BASE_PATH],
  );

  const activeTabIndex = NAV_ITEMS.findIndex(
    (item) => currentPath === item.path.replace(/\/$/, ""),
  );

  const toast = useToast();

  const { stationDetails, loadingStationDetails, fetchStation } =
    useKardaAdmin();
  const station = stationDetails[stationId!] ?? null;
  const isLoading = loadingStationDetails[stationId!] ?? false;

  useEffect(() => {
    if (!stationId) return;
    fetchStation(stationId).catch((err) => {
      if (isAxiosError(err)) {
        toast.error(err.response?.data?.message || err.message);
      } else {
        toast.error("Failed to load station.");
      }
    });
  }, [stationId, fetchStation, toast]);

  if (isLoading && !station) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4, px: { xs: 3, md: 5 }, pt: { xs: 1, md: 1.5 } }}>
      {/* Page header row */}
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/karda-admin")}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box flex={1}>
          <Typography variant="h5" fontWeight="bold">
            {station?.name ?? stationId}
          </Typography>
          {station?.id && (
            <Typography variant="body2" color="text.secondary">
              ID: {station.id}
            </Typography>
          )}
        </Box>
        {headerAction}
        {station?.metadata?.station_status && (
          <Chip
            label={String(station.metadata.station_status)}
            color={
              station.metadata.station_status === "active"
                ? "success"
                : "default"
            }
          />
        )}
      </Box>

      {/* Tab navigation */}
      <Tabs
        value={activeTabIndex === -1 ? false : activeTabIndex}
        onChange={(_, newValue) => navigate(NAV_ITEMS[newValue].path)}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            minHeight: 44,
            px: 2,
            gap: 0.75,
            "&.Mui-selected": {
              fontWeight: 600,
            },
          },
        }}
      >
        {NAV_ITEMS.map((item) => (
          <Tab
            key={item.path}
            label={item.label}
            icon={
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& > svg": { fontSize: 18 },
                }}
              >
                {item.icon}
              </Box>
            }
            iconPosition="start"
          />
        ))}
      </Tabs>

      <Outlet
        context={
          {
            setHeaderAction: stableSetHeaderAction,
          } satisfies StationDetailOutletContext
        }
      />
    </Box>
  );
};

export default KardaStationDetailPage;
